import { Controller } from "@hotwired/stimulus"

// Keep the actual form in its Stimulus scope. Fixed positioning lets it cross the scroller's
// clipping edge without cloning/reparenting the input (which would lose focus or typeahead state).
export default class extends Controller {
  static targets = ["anchor", "form"]

  connect() {
    this.header = document.querySelector(".search-v2-header")
    if (!this.header) return
    this.anchorTarget.style.minHeight = `${this.formTarget.offsetHeight}px`
    this.update()
  }

  update() {
    if (!this.header || this.element.style.display === "none") return
    cancelAnimationFrame(this.frame)
    this.frame = requestAnimationFrame(() => this.measure())
  }

  measure(force = false) {
    const anchor = this.anchorTarget.getBoundingClientRect()
    const header = this.header.getBoundingClientRect()
    // Hysteresis prevents a tiny scroll around the boundary from repeatedly toggling the form.
    const dock = anchor.top < header.bottom + (this.docked ? 32 : 16)
    if (dock === !!this.docked && !force) return
    const from = this.formTarget.getBoundingClientRect()
    this.animation?.cancel()
    this.docked = dock
    const brand = this.header.querySelector(".search-v2-header__brand").getBoundingClientRect()
    const actions = this.header.querySelector(".search-v2-header__actions").getBoundingClientRect()
    const compact = actions.left - brand.right < 320
    this.header.classList.toggle("search-v2-header--search-compact", dock && compact)
    const left = compact ? header.left : brand.right + 24
    const right = compact ? header.right - 52 : actions.left - 24
    const width = Math.min(640, right - left)
    const destination = dock ? {
      left: left + (right - left - width) / 2,
      top: brand.top + (brand.height - from.height) / 2,
      width
    } : anchor
    const geometry = rect => ({ left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px` })
    this.formTarget.classList.add("provider-landing__search-docked")
    Object.assign(this.formTarget.style, geometry(destination))
    const duration = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 320
    this.animation = this.formTarget.animate([geometry(from), geometry(destination)], {
      duration, easing: "cubic-bezier(.22, 1, .36, 1)"
    })
    this.animation.onfinish = () => {
      if (!this.docked) this.clearPosition()
    }
  }

  resize() {
    if (this.header && this.element.style.display !== "none") this.measure(true)
  }

  clearPosition() {
    if (!this.hasFormTarget) return
    this.formTarget.classList.remove("provider-landing__search-docked")
    for (const property of ["left", "top", "width"]) this.formTarget.style.removeProperty(property)
  }

  reset() {
    cancelAnimationFrame(this.frame)
    this.animation?.cancel()
    this.docked = false
    this.header?.classList.remove("search-v2-header--search-compact")
    this.clearPosition()
  }

  disconnect() { this.reset() }
}
